import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Box, CssBaseline, Paper, Tooltip, Typography } from '@mui/material';
import clsx from 'clsx';
import LPAppBar from '../../app/components/LPAppBar';
import LPDrawer from '../../app/components/LPDrawer';
import ShiftHome from '../../app/components/ShiftHome';
import Utils from '../../felles/Utils';
import MenuItemTarget from '../../models/MenuItemTarget';
import UISize from '../../models/UISize';
import * as actions from '../../store/actions/index';
import * as actionsNO05 from '../../store/actions/indexNO05';
import { AppState } from '../../store/rootReducer';
import { useStyles } from '../../assets/styles/DefaultPageStyle';
import NO05StatusCards from './components/NO05StatusCards';
import NO05Search from './components/NO05Search';
import NO05List from './components/NO05List';

export default function NO05Page() {
    const { classes } = useStyles();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const screenSize: UISize = useSelector((state: AppState) => state.ui.screenSize);
    const open = screenSize.width > 0 && screenSize.width < 1200 ? false : true;
    const button: boolean = useSelector((state: AppState) => state.ui.isSandwichButtonTurnedOn);
    const n: MenuItemTarget = useSelector((state: AppState) => state.menuItem.menuItemTarget);
    const hasAutoLoaded = useRef(false);
    const [showCrossFilters, setShowCrossFilters] = useState(false);

    const stats = useSelector((state: AppState) => state.NO05AapneOrdre.stats);
    const statsLoading = useSelector((state: AppState) => state.NO05AapneOrdre.statsLoading);
    const selectedStatus = useSelector((state: AppState) => state.NO05AapneOrdre.selectedStatus);
    const lastSearchParams = useSelector((state: AppState) => state.NO05AapneOrdre.lastSearchParams);
    const companyId = useSelector((state: AppState) => state.auth.userJWT?.Department ?? '');

    // Auto-load all open orders once on mount only — clear does not re-trigger
    useEffect(() => {
        if (!hasAutoLoaded.current && companyId) {
            hasAutoLoaded.current = true;
            dispatch(actionsNO05.NO05SearchRequest({ companyId, skipPaging: true }));
        }
    }, [dispatch, companyId]);

    useEffect(() => {
        return () => {
            dispatch(actionsNO05.NO05ClearComponentStore());
        };
    }, [dispatch]);

    // Card click: toggle filter. Clicking the active card deselects back to Alle ordre.
    const handleStatusSelect = (status: string) => {
        const next = selectedStatus === status && status !== 'Alle ordre' ? 'Alle ordre' : status;
        dispatch(actionsNO05.NO05SetStatus(next));
    };

    const handlePressedKey = (event: React.KeyboardEvent<HTMLDivElement>) => {
        const { key } = event;
        if ((event.shiftKey && key === 'End') || key === 'F3') {
            event.preventDefault();
            dispatch(actionsNO05.NO05ClearComponentStore());
            dispatch(actions.menuItemSetTarget(
                new MenuItemTarget(n.rootItem, n.item !== '' ? n.item : n.rootItem, '', '', n.tilpasset, n.prodSynlig, n.kodeSynlig, n.labelSynlig, n.menuSynlig, n.hasChildren, n.title)
            ));
            dispatch(actions.menuItemSetSelected([n.rootItem, n.item + n.rootItem]));
            navigate(Utils.HOMEPAGE_CONTEXT + '/menu');
        }
    };

    return (
        <div className={classes.root} tabIndex={0} onKeyDown={handlePressedKey}>
            <ShiftHome />
            <CssBaseline />
            <LPDrawer />
            <main
                className={clsx(classes.content, { [classes.contentShift]: button ? true : open })}
                style={{ overflow: 'hidden', height: 'calc(100vh - 45px)', display: 'flex', flexDirection: 'column' }}
            >
                <LPAppBar ismenuvisible={true} />

                {/* Status cards — shown once a search has run, or while the background full-order
                    fetch that computes them is still in flight (NO05StatusCards shows its own
                    skeleton via `loading`) so the spinner appears right away instead of only once
                    stats actually land. */}
                {(stats.length > 0 || statsLoading) && (
                    <Paper elevation={0} sx={{ px: 2, pt: 1, pb: 0, borderBottom: '1px solid #e0e0e0', flexShrink: 0, position: 'relative' }}>
                        <Tooltip
                            title={
                                <Box sx={{ maxWidth: 420, maxHeight: '70vh', overflowY: 'auto' }}>
                                    <Typography sx={{ fontSize: '0.85rem', mb: 1.5 }}>
                                        Programmet viser alle ordre som ikke er fakturert fordelt på ulike statuser.
                                        Klikke på de ulike statusboksene for å se ordrer med denne statusen.
                                    </Typography>

                                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, mb: 0.5, textDecoration: 'underline' }}>Statusboksene:</Typography>
                                    <Typography sx={{ fontSize: '0.85rem', mb: 1.5 }}>
                                        Viser antall ordre, total ordresum (uten mva), gjennomsnittlig bruttofortjeneste, hvis tilgang i TAB1, antall varer bestilt / antall varer levert.
                                    </Typography>

                                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, mb: 0.5, textDecoration: 'underline' }}>På tvers av status:</Typography>
                                    <Typography sx={{ fontSize: '0.85rem', mb: 1.5 }}>
                                        Dette er en samling av ulike ordregrupper på tvers av status.
                                    </Typography>

                                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, mb: 0.5, textDecoration: 'underline' }}>Hurtig søk:</Typography>
                                    <Typography sx={{ fontSize: '0.85rem', mb: 1.5 }}>
                                        Søk på ordrenummer eller kundenummer for alle ikke fakturerte ordre på en kunde. Statusboksene vil da vise resultatet på dette søket.
                                    </Typography>

                                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, mb: 0.5, textDecoration: 'underline' }}>Avansert søk:</Typography>
                                    <Typography sx={{ fontSize: '0.85rem', mb: 0.5 }}>
                                        Her er det flere søkemuligheter for søk på ordre som ikke er fakturert. Klikk på Søk eller tast Enter etter at ønsket søkekriterier er fylt ut.
                                    </Typography>
                                    <Box component="ul" sx={{ m: 0, mb: 1.5, pl: 2.5 }}>
                                        <Typography component="li" sx={{ fontSize: '0.85rem', mb: 0.5 }}><b>Rekv.nr</b> – søk på hele eller deler av et rekvisisjonsnummer</Typography>
                                        <Typography component="li" sx={{ fontSize: '0.85rem', mb: 0.5 }}><b>Alfa/artikkel</b> – søk på en alfakode eller kombinert med artikkelnummer</Typography>
                                        <Typography component="li" sx={{ fontSize: '0.85rem', mb: 0.5 }}><b>OM</b> – ordremottaker</Typography>
                                        <Typography component="li" sx={{ fontSize: '0.85rem', mb: 0.5 }}><b>Lokasjon</b> – søk på varelinje ut ifra lokasjonen som er registrert på varen</Typography>
                                        <Typography component="li" sx={{ fontSize: '0.85rem', mb: 0.5 }}><b>Vareadresse</b> – søk fra feltene vareadresse 1–4 på ordren</Typography>
                                        <Typography component="li" sx={{ fontSize: '0.85rem', mb: 0.5 }}><b>Avdeling</b> – søk på ordre i avdeling hvis det brukes flere avdelinger</Typography>
                                        <Typography component="li" sx={{ fontSize: '0.85rem', mb: 0.5 }}><b>Lagerkode</b> – søk på ordre med lagerkode hvis det brukes flere lagerkoder</Typography>
                                        <Typography component="li" sx={{ fontSize: '0.85rem' }}><b>Nullstill</b> – blanker ut alle felter</Typography>
                                    </Box>

                                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, mb: 0.5, textDecoration: 'underline' }}>Tabell med ordrer:</Typography>
                                    <Typography sx={{ fontSize: '0.85rem', mb: 0.5 }}>
                                        Ved søk som gir mer enn én linje vises ordrene i synkende rekkefølge på dato – nyeste ordre øverst.
                                    </Typography>
                                    <Box component="ul" sx={{ m: 0, mb: 1.5, pl: 2.5 }}>
                                        <Typography component="li" sx={{ fontSize: '0.85rem', mb: 0.5 }}>Ordrenummer med link til visning av ordren</Typography>
                                        <Typography component="li" sx={{ fontSize: '0.85rem', mb: 0.5 }}>Fakturanummer med link til visning av fakturaen</Typography>
                                        <Typography component="li" sx={{ fontSize: '0.85rem', mb: 0.5 }}>Dato for opprettelse av ordren</Typography>
                                        <Typography component="li" sx={{ fontSize: '0.85rem', mb: 0.5 }}>Kundenummer og kundenavn</Typography>
                                        <Typography component="li" sx={{ fontSize: '0.85rem', mb: 0.5 }}>Frakt/sporing vises hvis dette er registrert, hvis ikke vises printernavn</Typography>
                                        <Typography component="li" sx={{ fontSize: '0.85rem', mb: 0.5 }}>Rekv.nr fra ordre</Typography>
                                        <Typography component="li" sx={{ fontSize: '0.85rem', mb: 0.5 }}>Ordrestatus</Typography>
                                        <Typography component="li" sx={{ fontSize: '0.85rem', mb: 0.5 }}>Ordremottaker</Typography>
                                        <Typography component="li" sx={{ fontSize: '0.85rem', mb: 0.5 }}>Antall bestilt / antall levert – viser antall i stk., ikke antall varelinjer</Typography>
                                        <Typography component="li" sx={{ fontSize: '0.85rem', mb: 0.5 }}>Sum ordre uten mva</Typography>
                                        <Typography component="li" sx={{ fontSize: '0.85rem' }}>Bruttofortjeneste på ordren – vises kun for de som har tilgang til å se dette i program TAB1</Typography>
                                    </Box>

                                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, mb: 0.5, textDecoration: 'underline' }}>Uttrekk til Excel:</Typography>
                                    <Typography sx={{ fontSize: '0.85rem', mb: 0.5 }}>
                                        Tabellen som vises på skjerm etter dine søkekriterier kan lastes ned i Excel i to ulike varianter:
                                    </Typography>
                                    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                                        <Typography component="li" sx={{ fontSize: '0.85rem', mb: 0.5 }}><b>Excel</b> – tabellen slik du ser på skjerm, med én linje pr. ordre</Typography>
                                        <Typography component="li" sx={{ fontSize: '0.85rem' }}><b>Med ordrelinjer</b> – fil med alle ordrelinjer fra valgte utplukk</Typography>
                                    </Box>
                                </Box>
                            }
                            placement="bottom-end"
                            arrow
                        >
                            <Box
                                sx={{
                                    width: 22,
                                    height: 22,
                                    borderRadius: '50%',
                                    backgroundColor: '#2e7d32',
                                    color: 'white',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    fontSize: '0.9rem',
                                    fontWeight: 'bold',
                                    userSelect: 'none',
                                    position: 'absolute', top: 8, right: 10, zIndex: 1,
                                }}
                            >
                                i
                            </Box>
                        </Tooltip>
                        <NO05StatusCards
                            stats={stats}
                            loading={statsLoading}
                            selectedStatus={selectedStatus}
                            onSelect={handleStatusSelect}
                            showCrossFilters={showCrossFilters}
                            onToggleCrossFilters={() => setShowCrossFilters(v => !v)}
                        />
                    </Paper>
                )}

                {/* Search */}
                <NO05Search />

                {/* List — filters client-side by selectedStatus, hidden until search performed */}
                <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                    <NO05List />
                </Box>
            </main>
        </div>
    );
}
